import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Flame, Gauge } from "lucide-react";
import { api, rp } from "@/lib/api";

const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";

const Delta = ({ v, invert }) => {
  if (v === null || v === undefined) return <span className="text-ink-soft">—</span>;
  const good = invert ? v <= 0 : v >= 0;
  return (
    <span className={good ? "text-emerald-700" : "text-lm-red"}>
      {v > 0 ? "+" : ""}{v}%
    </span>
  );
};

const RankCard = ({ title, icon: Icon, rows, render, testid }) => (
  <div className="bg-white border border-black/10 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm font-semibold">
      <Icon size={14} className="text-lm-red" /> {title}
    </div>
    <div className="mt-2 space-y-1.5">
      {rows.length === 0 && <div className="text-xs text-ink-soft">Belum ada data bulan ini.</div>}
      {rows.map((r, i) => (
        <div key={r.name} className="flex justify-between text-xs">
          <span>{i + 1}. {r.name}</span><span className="font-medium">{render(r)}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function HubPerformance() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/reports/hub-performance", { params: { month } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Gagal memuat rapor"));
  }, [month]);

  const months = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 18; i += 1) {
      const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(dt.toISOString().slice(0, 7));
    }
    return out;
  }, []);

  const csv = `${process.env.REACT_APP_BACKEND_URL}/api/reports/hub-performance?month=${month}&format=csv`;

  return (
    <div data-testid="hub-performance-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Rapor Kinerja Hub</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Hub mana yang paling ramai dan paling sering telat, per bulan, dengan pembanding bulan lalu.
          </p>
        </div>
        <div className="flex gap-2">
          <select className={sel} data-testid="hp-month" value={month}
            onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <a href={csv} data-testid="hp-export-csv"
            className="rounded-md border border-black/15 px-4 py-2.5 text-sm inline-flex items-center gap-1.5">
            <Download size={14} /> CSV
          </a>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Booking", d?.totals?.bookings, false], ["Pendapatan", d?.totals?.revenue, true],
          ["Komisi Hub", d?.totals?.commission, true], ["Telat kembali", d?.totals?.late_returns, false],
          ["Unit terdaftar", d?.totals?.units, false]].map(([l, v, money]) => (
          <div key={l} className="bg-white border border-black/10 rounded-lg p-4">
            <div className="text-[10px] uppercase tracking-widest text-ink-soft">{l}</div>
            <div className="mt-1 font-display text-2xl"
              data-testid={`hp-total-${l.toLowerCase().replace(/\s/g, "-")}`}>
              {money ? rp(v || 0) : (v ?? 0)}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        <RankCard title="Paling ramai" icon={Flame} testid="hp-rank-busiest"
          rows={d?.rankings?.busiest || []} render={(r) => `${r.bookings} booking`} />
        <RankCard title="Paling sering telat" icon={AlertTriangle} testid="hp-rank-late"
          rows={d?.rankings?.most_late || []}
          render={(r) => `${r.late_returns}× · ${r.late_rate}% · ~${r.avg_late_hours} jam`} />
        <RankCard title="Pemakaian unit tertinggi" icon={Gauge} testid="hp-rank-utilization"
          rows={d?.rankings?.best_utilization || []} render={(r) => `${r.utilization_percent}%`} />
      </div>

      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm" data-testid="hp-table">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Hub</th><th className="p-3">Booking</th><th className="p-3">Pendapatan</th>
              <th className="p-3">Komisi</th><th className="p-3">Sewa Keluar</th>
              <th className="p-3">Main di Venue</th><th className="p-3">Pemakaian</th>
              <th className="p-3">Telat</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {(d?.hubs || []).map((h) => (
              <tr key={h.hub_id} data-testid={`hp-row-${h.code}`}>
                <td className="p-3 font-medium">{h.name}
                  <div className="text-[11px] text-ink-soft">
                    {h.units} unit{h.status !== "active" ? ` · ${h.status}` : ""}
                  </div></td>
                <td className="p-3">{h.bookings}
                  <div className="text-[11px]"><Delta v={h.delta?.bookings} /></div></td>
                <td className="p-3">{rp(h.revenue)}
                  <div className="text-[11px]"><Delta v={h.delta?.revenue} /></div></td>
                <td className="p-3">{rp(h.commission)}</td>
                <td className="p-3">{h.by_category.external_rental.bookings}
                  <div className="text-[11px] text-ink-soft">{rp(h.by_category.external_rental.revenue)}</div></td>
                <td className="p-3">{h.by_category.play_at_venue.bookings}
                  <div className="text-[11px] text-ink-soft">{rp(h.by_category.play_at_venue.revenue)}</div></td>
                <td className="p-3">{h.utilization_percent}%
                  <div className="text-[11px] text-ink-soft">{h.booked_hours}/{h.capacity_hours} jam</div></td>
                <td className={`p-3 ${h.late_returns ? "text-lm-red" : ""}`}>{h.late_returns}
                  <div className="text-[11px] text-ink-soft">{h.late_rate}% · ~{h.avg_late_hours} jam</div></td>
              </tr>
            ))}
            {d && (d.hubs || []).length === 0 && (
              <tr><td colSpan={8} className="p-4 text-ink-soft">Belum ada Hub.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {d?.note && <p className="mt-3 text-xs text-ink-soft" data-testid="hp-note">{d.note}</p>}
    </div>
  );
}
